import { QueryClient, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import axios from 'axios';

import {
  GENERATE_CUSTOM_VALUE,
  GET_EVENT_TRIBUTE_VIDEOS,
  GET_TRIBUTE_VIDEO_BY_ASSET_ID_QUERY_KEY,
  GET_TRIBUTE_VIDEO_EXPORT_STATUS_QUERY_KEY,
  IMAGE_MOVEMENTS,
  MOVEMENT_BUNDLES,
  TRIBUTE_VIDEO_API_URL,
  TRIBUTE_VIDEO_BY_ID_QUERY_KEY
} from '@/constants';
import { apiUrl, getQueryParams } from '@/helpers';
import { ErrorResponse, Options, UpdateCreateTributeVideoPayload } from '@/types';

export const tributeVideoEndpoints = {
  getEventTributeVideos: (
    eventId: string,
    pageSize: number,
    tagIds: string[],
    cursor: number,
    videoThemeId?: string,
    status?: string
  ) =>
    axios.get(
      `${apiUrl()}${TRIBUTE_VIDEO_API_URL}/event/${eventId}${getQueryParams({
        'paging.PageSize': pageSize as number,
        'paging.Cursor': cursor as number,
        'filter.VideoThemeId': videoThemeId as string,
        'filter.Status': status as string,
        'filter.TagIds': tagIds
      })}`
    ),
  createTributeVideo: (payload: UpdateCreateTributeVideoPayload) =>
    axios.post(`${apiUrl()}${TRIBUTE_VIDEO_API_URL}`, payload),
  getTributeVideo: (id: number) => axios.get(`${apiUrl()}${TRIBUTE_VIDEO_API_URL}/${id}`),
  deleteTributeVideo: (id: number) => axios.delete(`${apiUrl()}${TRIBUTE_VIDEO_API_URL}/${id}`),
  updateTributeVideo: (payload: UpdateCreateTributeVideoPayload) =>
    axios.put(`${apiUrl()}${TRIBUTE_VIDEO_API_URL}`, payload),
  generateTributeVideo: (payload: { id: string; isPreview: boolean }) =>
    axios.post(
      `${apiUrl()}${TRIBUTE_VIDEO_API_URL}/generatevideo/${payload.id}/${payload.isPreview}`
    ),
  updateTributeVideoOptions: (payload: {
    id: number;
    eventId: number;
    options: {
      textsEnabled: boolean;
      imageBordersEnabled: boolean;
    };
  }) => axios.patch(`${apiUrl()}${TRIBUTE_VIDEO_API_URL}`, payload),
  updateTributeVideoName: (payload: { id: number; name: string }) =>
    axios.patch(`${apiUrl()}${TRIBUTE_VIDEO_API_URL}/updateName`, payload),
  getMovementBundles: () => axios.get(`${apiUrl()}${TRIBUTE_VIDEO_API_URL}/${MOVEMENT_BUNDLES}`),
  generateCustomAttributeValues: (eventId: number) =>
    axios.get(`${apiUrl()}${TRIBUTE_VIDEO_API_URL}/${GENERATE_CUSTOM_VALUE}/${eventId}`),
  getImageMovementsByMovementBundleId: (movementBundleId: number) =>
    axios.get(
      `${apiUrl()}${TRIBUTE_VIDEO_API_URL}/${MOVEMENT_BUNDLES}/${movementBundleId}/${IMAGE_MOVEMENTS}`
    ),
  getTributeVideoByAssetId: (assetId: number) =>
    axios.get(`${apiUrl()}${TRIBUTE_VIDEO_API_URL}/getByAssetId/${assetId}`)
};

export const useGetEventTributeVideos = (
  eventId: string,
  pageSize: number,
  tagIds?: string[],
  keepPreviousData?: boolean,
  enabled?: boolean,
  videoThemeId?: string,
  status?: string,
  refetchInterval?: number
) => {
  return useInfiniteQuery(
    [GET_EVENT_TRIBUTE_VIDEOS, { eventId, pageSize, tagIds, videoThemeId, status }],
    ({ pageParam }) =>
      tributeVideoEndpoints.getEventTributeVideos(
        eventId,
        pageSize,
        tagIds as string[],
        pageParam,
        videoThemeId,
        status
      ),
    {
      refetchInterval: refetchInterval,
      keepPreviousData: keepPreviousData ?? true,
      enabled: !!eventId && enabled,
      getNextPageParam: (lastPage) => {
        if (lastPage && lastPage.data?.nextCursor !== undefined) {
          return lastPage.data?.nextCursor;
        }
        return undefined;
      },
      getPreviousPageParam: (firstPage) => {
        if (firstPage && firstPage.data?.previousCursor !== undefined) {
          return firstPage.data?.previousCursor;
        }
        return undefined;
      }
    }
  );
};

export const useCreateTributeVideoMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(tributeVideoEndpoints.createTributeVideo, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([GET_EVENT_TRIBUTE_VIDEOS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useGetTributeVideoQuery = (id: number, options?: Options, enabled?: boolean) => {
  return useQuery(
    [TRIBUTE_VIDEO_BY_ID_QUERY_KEY, { id }],
    () => tributeVideoEndpoints.getTributeVideo(id),
    {
      retry: false,
      enabled: !!id && (enabled ?? true),
      onSuccess: options?.onSuccess,
      onError: (error) => {
        options?.onError(error as ErrorResponse);
      }
    }
  );
};

export const useDeleteTributeVideoMutation = (queryClient: QueryClient, options: Options) => {
  return useMutation(tributeVideoEndpoints.deleteTributeVideo, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([GET_EVENT_TRIBUTE_VIDEOS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateTributeVideo = (queryClient: QueryClient, options: Options) => {
  return useMutation(tributeVideoEndpoints.updateTributeVideo, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([GET_EVENT_TRIBUTE_VIDEOS]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};
export const useGenerateTributeVideo = (queryClient: QueryClient, options: Options) => {
  return useMutation(tributeVideoEndpoints.generateTributeVideo, {
    onSuccess: (response) => {
      options.onSuccess(response);
      queryClient.invalidateQueries([GET_TRIBUTE_VIDEO_EXPORT_STATUS_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateTributeVideoOptions = (queryClient: QueryClient, options: Options) => {
  return useMutation(tributeVideoEndpoints.updateTributeVideoOptions, {
    onSuccess: () => {
      options.onSuccess();
      queryClient.invalidateQueries([TRIBUTE_VIDEO_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options.onError(error as ErrorResponse);
    }
  });
};

export const useUpdateTributeVideoName = (queryClient: QueryClient, options?: Options) => {
  return useMutation(tributeVideoEndpoints.updateTributeVideoName, {
    onSuccess: () => {
      options?.onSuccess();
      queryClient.invalidateQueries([TRIBUTE_VIDEO_BY_ID_QUERY_KEY]);
    },
    onError: (error) => {
      options?.onError(error as ErrorResponse);
    }
  });
};

export const useGetMovementBundles = () => {
  return useQuery([MOVEMENT_BUNDLES], () => tributeVideoEndpoints.getMovementBundles(), {});
};

export const useGenerateCustomAttributesTributeVideoQuery = (
  eventId: number,
  enabled?: boolean
) => {
  return useQuery(
    [GET_EVENT_TRIBUTE_VIDEOS, { eventId }],
    () => tributeVideoEndpoints.generateCustomAttributeValues(eventId),
    {
      enabled: !!eventId && (enabled ?? true)
    }
  );
};

export const useGetImageMovementsByMovementBundleId = (movementBundleId: number) => {
  return useQuery(
    [IMAGE_MOVEMENTS, { movementBundleId }],
    () => tributeVideoEndpoints.getImageMovementsByMovementBundleId(movementBundleId),
    {}
  );
};

export const useGetTributeVideoByAssetId = (assetId: number | undefined, options?: Options) => {
  return useQuery(
    [GET_TRIBUTE_VIDEO_BY_ASSET_ID_QUERY_KEY, { assetId }],
    () => tributeVideoEndpoints.getTributeVideoByAssetId(assetId!),
    {
      retry: false,
      enabled: false,
      onSuccess: (response) => {
        options?.onSuccess?.(response);
      },
      onError: (error) => {
        options?.onError?.(error as ErrorResponse);
      }
    }
  );
};
